import React, {useEffect, useState} from 'react'
import {toast, ToastContainer} from 'react-toastify'
import '../../../styles/custom-toast.css'
import {StudyGuide} from '../../../redux/studyGuides'

import ARECommunity from '../../../images/icons/new-dashboard/are-community.svg'
import ARELive from '../../../images/icons/new-dashboard/are-live.svg'
import CongratulationsIcon from '../../../images/icons/new-dashboard/toast-congratulations.svg'
import CongratulationsIcon2 from '../../../images/icons/new-dashboard/toast-congratulations-2.svg'
import PrayIcon from '../../../images/icons/new-dashboard/toast-pray.svg'
import BlogIcon from '../../../images/icons/new-dashboard/toast-blog.svg'
import {useSelector} from 'react-redux'
import {selectMember, selectMemberAccess} from '../../../redux/members'
import {useAuth} from '../../../components/AuthProvider'
import {useAppDispatch} from '../../../redux/configureStore'
import {handleUpgrade} from '../../../utils/upgradeHelper'
import {Divisions, getDivisionDetails} from '../../../constants/divisions'

type Props = {
  studyGuide?: StudyGuide
}

type ToastData = {
  title?: JSX.Element
  description?: JSX.Element
  img?: JSX.Element
}

const View = ({studyGuide}: Props) => {
  const [isToastOpen, setIsToastOpen] = useState(false)
  const auth = useAuth()
  const dispatch = useAppDispatch()
  const access = useSelector(selectMemberAccess)
  const member = useSelector(selectMember)

  const hasWorkshopAccess =
    access && Object.keys(access).length !== 0
      ? Number(access.are?.workshops?.value) === 1
      : false

  const currentDay = studyGuide?.assets?.find(a => a.inProgress)
  const studyDay = currentDay?.studyDay
  const timeWindow = studyGuide?.timeWindow
  const abbr = studyGuide?.abbreviation as Divisions
  const divisionDetails = getDivisionDetails(abbr)

  const loadToastData = (): ToastData => {
    if (
      (studyDay === 1 && timeWindow === 30) ||
      (studyDay === 1 && timeWindow === 60)
    ) {
      return {
        title: <h3>Welcome to Day 1 of your Study Plan!</h3>,
        description: (
          <p>
            Check out this{' '}
            <a
              href="https://www.blackspectacles.com/podcast/are-live-study-tactics-to-pass-the-are-on-your-first-try"
              target="_blank"
            >
              ARE Live episode
            </a>{' '}
            to learn how to take advantage of your Black Spectacles' suite of
            study materials and use each to their greatest potential.
          </p>
        ),
      }
    }

    if (
      (studyDay === 3 && timeWindow === 30) ||
      (studyDay === 4 && timeWindow === 60)
    ) {
      return {
        title: (
          <h3>
            Have you visited the{' '}
            <a
              href="https://community.blackspectacles.com/t/welcome-to-the-are-community-start-here/6747"
              target="_blank"
            >
              ARE Community
            </a>{' '}
            yet?
          </h3>
        ),
        description: (
          <p>
            It's a great place to ask questions and engage in discussions with
            peers and licensed architects!
          </p>
        ),
        img: <img src={ARECommunity} alt="ARE Community" />,
      }
    }

    if (
      (studyDay === 5 && timeWindow === 30) ||
      (studyDay === 12 && timeWindow === 60)
    ) {
      return {
        title: (
          <h3>
            <img src={BlogIcon} alt="Blog" /> Blog
          </h3>
        ),
        description: (
          <p>
            Flashcards are a great way to quickly test your knowledge and
            identify gaps in your studying.
            <br />
            Visit this{' '}
            <a
              href="https://www.blackspectacles.com/blog/tips-to-incorporate-flashcards-into-your-study-process"
              target="_blank"
            >
              blog post
            </a>{' '}
            for tips on how to best incorporate flashcards into your study
            process.
          </p>
        ),
      }
    }

    if (
      (studyDay === 7 && timeWindow === 30) ||
      (studyDay === 7 && timeWindow === 60)
    ) {
      return {
        title: (
          <h3>
            <img src={CongratulationsIcon} alt="Congratulations" />{' '}
            Congratulations!
          </h3>
        ),
        description: <p>You've completed one week of consistent studying!</p>,
      }
    }

    if (
      (studyDay === 10 && timeWindow === 30) ||
      (studyDay === 17 && timeWindow === 60)
    ) {
      return {
        title: <h3>Not sure how to approach case study items?</h3>,
        description: (
          <p>
            RSVP for the{' '}
            <a
              href="https://learn.blackspectacles.com/virtual-workshops"
              target="_blank"
            >
              upcoming Virtual Workshop
            </a>{' '}
            to join a collaborative learning environment, where you can tackle
            the difficult problems with peers and a registered architect coach.
            {!hasWorkshopAccess && (
              <>
                <br />
                <a
                  href="#"
                  onClick={handleUpgrade(
                    dispatch,
                    auth,
                    member,
                    'study guide workshop toast paywall',
                  )}
                >
                  Upgrade your subscription to an Expert plan
                </a>{' '}
                to get access to this material.
              </>
            )}
          </p>
        ),
      }
    }

    if (
      (studyDay === 12 && timeWindow === 30) ||
      (studyDay === 40 && timeWindow === 60)
    ) {
      return {
        img: <img src={ARELive} alt="ARE Live" />,
        description: (
          <p>
            Don't forget to register for the{' '}
            <a href="https://www.blackspectacles.com/podcast" target="_blank">
              upcoming ARE Live!
            </a>{' '}
            We also have a library of past episodes, including mock exams,
            available for viewing.
            <br />
            Check our most recent{' '}
            <a href={divisionDetails?.mockExam} target="_blank">
              {abbr} Mock Exam
            </a>
          </p>
        ),
      }
    }

    if (
      (studyDay === 15 && timeWindow === 30) ||
      (studyDay === 30 && timeWindow === 60)
    ) {
      return {
        title: (
          <h3>
            <img src={PrayIcon} alt="Halfway" /> You are halfway there!
          </h3>
        ),
        description: (
          <p>
            Looking for some additional tips and strategies on how to pass the
            ARE 5.0? We got you - check out this{' '}
            <a
              href="https://www.blackspectacles.com/podcast/are-live-how-to-pass-the-are-5.0-in-2025"
              target="_blank"
            >
              ARE Live episode
            </a>
            .
          </p>
        ),
      }
    }

    if (
      (studyDay === 17 && timeWindow === 30) ||
      (studyDay === 35 && timeWindow === 60)
    ) {
      return {
        title: <h3>Reminder:</h3>,
        description: (
          <p>
            If you haven't already done so, be sure to{' '}
            <a href="https://login.ncarb.org/" target="_blank">
              schedule your exam
            </a>
            ! Having a date marked on your calendar will help you stay motivated
            and accountable as you work towards your goal.
          </p>
        ),
      }
    }

    if (
      (studyDay === 19 && timeWindow === 30) ||
      (studyDay === 45 && timeWindow === 60)
    ) {
      return {
        title: (
          <h3>
            <img src={BlogIcon} alt="Blog" /> Blog
          </h3>
        ),
        description: (
          <p>
            How does NCARB develop questions for the ARE? Check out this{' '}
            <a
              href="https://www.blackspectacles.com/blog/how-ncarb-develops-are-items-and-cut-scores"
              target="_blank"
            >
              blog post
            </a>{' '}
            and get into the mindset of the exam creator, so you'll know how to
            tackle those tricky questions on the exam!
          </p>
        ),
      }
    }

    if (
      (studyDay === 25 && timeWindow === 30) ||
      (studyDay === 50 && timeWindow === 60)
    ) {
      return {
        description: (
          <p>
            Did you know that we have{' '}
            <a
              href="https://community.blackspectacles.com/c/practice-quizzes/37"
              target="_blank"
            >
              practice quizzes on the ARE Community
            </a>
            ? It is a great way to add some additional practice questions into
            your studying!
          </p>
        ),
        img: <img src={ARECommunity} alt="ARE Community" />,
      }
    }

    if (
      (studyDay === 27 && timeWindow === 30) ||
      (studyDay === 54 && timeWindow === 60)
    ) {
      return {
        title: (
          <h3>
            <img src={BlogIcon} alt="Blog" /> Blog
          </h3>
        ),
        description: (
          <p>
            Test Day is almost here! Check out this{' '}
            <a
              href="https://www.blackspectacles.com/blog/10-tips-for-surviving-and-conquering-exam-day"
              target="_blank"
            >
              blog post
            </a>{' '}
            to help you prepare for the big day.
          </p>
        ),
      }
    }

    if (
      (studyDay === 30 && timeWindow === 30) ||
      (studyDay === 60 && timeWindow === 60)
    ) {
      return {
        title: (
          <h3>
            <img src={CongratulationsIcon2} alt="Congratulations" />{' '}
            Congratulations!
          </h3>
        ),
        description: (
          <p>
            You are now ready to conquer your exam. Good luck and don't forget
            to{' '}
            <a
              href="https://learn.blackspectacles.com/profile/settings/are_progress"
              target="_blank"
            >
              report your results
            </a>{' '}
            on your dashboard. You got this!
          </p>
        ),
      }
    }

    return {
      title: undefined,
      description: undefined,
      img: undefined,
    }
  }

  const RenderAlertBox = () => (
    <div>
      {loadToastData().img}
      {loadToastData().title}
      {loadToastData().description}
    </div>
  )

  useEffect(() => {
    const toastData = loadToastData()
    if (!isToastOpen && toastData.description) {
      setIsToastOpen(true)
      toast(<RenderAlertBox />, {
        position: 'bottom-center',
        autoClose: false,
        closeOnClick: false,
        className: 'custom-toast',
      })
    }
  }, [studyDay])

  return <ToastContainer />
}

export default View
